import React, { useEffect, useMemo, useRef, useState } from 'react';

const OVERALL_OPTION = 'Overall';
const OVERALL_LABEL = 'All companies';
const OVERALL_TOOLTIP = 'Combined total across all companies';

const PartnerComparisonDropdown = ({
  selectedValues,
  companyOptions,
  onApplySelection,
  label = 'Compare Partner Companies',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftSelectedValues, setDraftSelectedValues] = useState(selectedValues);
  const wrapRef = useRef(null);

  const options = useMemo(
    () => [{ value: OVERALL_OPTION, label: OVERALL_LABEL }, ...companyOptions.map((company) => ({
      value: company,
      label: company,
    }))],
    [companyOptions]
  );

  useEffect(() => {
    if (!isOpen) return;
    setDraftSelectedValues(selectedValues);
  }, [isOpen, selectedValues]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const normalizeSelection = (values) => {
    if (values.includes(OVERALL_OPTION) || values.length === 0) return [OVERALL_OPTION];
    return values;
  };

  const toggleOption = (value) => {
    const hasOverall = draftSelectedValues.includes(OVERALL_OPTION);
    const isSelected = draftSelectedValues.includes(value);

    if (value === OVERALL_OPTION) {
      setDraftSelectedValues([OVERALL_OPTION]);
      return;
    }

    if (isSelected) {
      const next = draftSelectedValues.filter((item) => item !== value);
      setDraftSelectedValues(next.length ? next : [OVERALL_OPTION]);
      return;
    }

    if (hasOverall) {
      setDraftSelectedValues([value]);
      return;
    }

    setDraftSelectedValues([...draftSelectedValues, value]);
  };

  const handleApply = () => {
    onApplySelection(normalizeSelection(draftSelectedValues));
    setIsOpen(false);
  };

  const handleCancel = () => {
    setDraftSelectedValues(selectedValues);
    setIsOpen(false);
  };

  const handleReset = () => {
    const resetSelection = [OVERALL_OPTION];
    setDraftSelectedValues(resetSelection);
    onApplySelection(resetSelection);
    setIsOpen(false);
  };

  return (
    <div className="lead-partner-dropdown-wrap analytics-compare-dropdown compact" ref={wrapRef}>
      <button
        type="button"
        className="lead-partner-dropdown-btn lead-compare-dropdown-btn analytics-compare-dropdown compact"
        onClick={() => {
          setIsOpen((open) => !open);
        }}
        aria-expanded={isOpen}
      >
        {label}
        <span className="lead-period-filter-arrow" aria-hidden="true">
          &#9662;
        </span>
      </button>
      {isOpen && (
        <div className="lead-partner-dropdown-menu lead-compare-dropdown-menu analytics-compare-menu partner-compare-menu">
          <div className="lead-compare-dropdown-helper compare-dropdown-helper partner-compare-helper">
            <span>Select companies to compare.</span>
            <span>Use {OVERALL_LABEL} for total trend.</span>
          </div>
          <ul className="lead-compare-options-list">
            {options.map((option) => {
              const selected = draftSelectedValues.includes(option.value);
              return (
                <li key={option.value} className="lead-company-list-item">
                  <button
                    type="button"
                    className={`lead-partner-dropdown-item lead-compare-dropdown-item partner-compare-option ${
                      selected ? 'lead-partner-dropdown-item-active' : ''
                    } ${
                      selected ? 'is-selected' : ''
                    } compare-dropdown-option`}
                    onClick={() => toggleOption(option.value)}
                    title={
                      option.value === OVERALL_OPTION
                        ? OVERALL_TOOLTIP
                        : option.label
                    }
                  >
                    <div className="partner-compare-option-content">
                      <input
                        type="checkbox"
                        checked={selected}
                        readOnly
                        tabIndex={-1}
                        className={`lead-compare-checkbox compare-dropdown-checkbox partner-compare-checkbox ${selected ? 'checked' : ''}`}
                      />
                      <div className="partner-compare-option-label-wrapper">
                        <span
                          className="compare-dropdown-label partner-compare-label"
                          title={option.value === OVERALL_OPTION ? OVERALL_TOOLTIP : option.label}
                        >
                          {option.label}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="lead-compare-dropdown-footer partner-compare-footer">
            <button type="button" className="lead-compare-dropdown-action" onClick={handleReset}>
              Reset
            </button>
            <button type="button" className="lead-compare-dropdown-action" onClick={handleCancel}>
              Cancel
            </button>
            <button type="button" className="lead-compare-dropdown-action lead-compare-dropdown-action-primary" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerComparisonDropdown;
